import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Chip,
  Divider
} from '@mui/material';
import {
  NavigateBefore,
  NavigateNext,
  Palette,
  Preview
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import { FestojoFormData, Theme } from '../../types/festejo';
import { StepComponentProps } from './FestojoFormContainer';

interface AppearanceFormProps extends StepComponentProps {}

interface ColorPickerState {
  primary: boolean;
  secondary: boolean;
}

const fontOptions = [
  { value: 'Roboto', label: 'Roboto (Padrão)' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'Inter', label: 'Inter' }
];

const presetThemes: Theme[] = [
  {
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    fontFamily: 'Roboto'
  },
  {
    primaryColor: '#2e7d32',
    secondaryColor: '#ff9800',
    fontFamily: 'Open Sans'
  },
  {
    primaryColor: '#7b1fa2',
    secondaryColor: '#ffc107',
    fontFamily: 'Montserrat'
  },
  {
    primaryColor: '#d32f2f',
    secondaryColor: '#1976d2',
    fontFamily: 'Lato'
  },
  {
    primaryColor: '#f57c00',
    secondaryColor: '#388e3c',
    fontFamily: 'Poppins'
  },
  {
    primaryColor: '#5d4037',
    secondaryColor: '#ff5722',
    fontFamily: 'Nunito'
  }
];

const AppearanceForm: React.FC<AppearanceFormProps> = ({
  formData,
  onUpdate,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep
}) => {
  const [colorPicker, setColorPicker] = useState<ColorPickerState>({
    primary: false,
    secondary: false
  });

  // Atualizar tema
  const handleThemeUpdate = (newTheme: Partial<Theme>) => {
    onUpdate({
      theme: {
        ...formData.theme,
        ...newTheme
      }
    });
  };

  // Aplicar tema predefinido
  const handleApplyPresetTheme = (theme: Theme) => {
    onUpdate({ theme });
  };

  // Alternar color picker
  const toggleColorPicker = (type: keyof ColorPickerState) => {
    setColorPicker(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Fechar color pickers
  const closeColorPickers = () => {
    setColorPicker({ primary: false, secondary: false });
  };

  // Preview do tema
  const PreviewCard: React.FC = () => (
    <Card 
      sx={{ 
        border: `2px solid ${formData.theme.primaryColor}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Header do preview */}
      <Box 
        sx={{ 
          backgroundColor: formData.theme.primaryColor,
          color: 'white',
          p: 2,
          textAlign: 'center'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: formData.theme.fontFamily,
            fontWeight: 600
          }}
        >
          {formData.title || 'Título do Festejo'}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            fontFamily: formData.theme.fontFamily,
            opacity: 0.9
          }}
        >
          {formData.padroeiro || 'Nome do Padroeiro'}
        </Typography>
      </Box>

      <CardContent>
        <Typography 
          variant="body1" 
          sx={{ 
            fontFamily: formData.theme.fontFamily,
            mb: 2
          }}
        >
          {formData.description || 'Descrição do festejo aparecerá aqui...'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label="Programação"
            sx={{ 
              backgroundColor: formData.theme.secondaryColor,
              color: 'white',
              fontFamily: formData.theme.fontFamily
            }}
          />
          <Chip 
            label="Galeria"
            variant="outlined"
            sx={{ 
              borderColor: formData.theme.primaryColor,
              color: formData.theme.primaryColor,
              fontFamily: formData.theme.fontFamily
            }}
          />
          <Chip 
            label="Contato"
            variant="outlined"
            sx={{ 
              borderColor: formData.theme.primaryColor,
              color: formData.theme.primaryColor,
              fontFamily: formData.theme.fontFamily
            }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            sx={{ 
              backgroundColor: formData.theme.primaryColor,
              fontFamily: formData.theme.fontFamily,
              mr: 1
            }}
          >
            Botão Primário
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ 
              borderColor: formData.theme.secondaryColor,
              color: formData.theme.secondaryColor,
              fontFamily: formData.theme.fontFamily
            }}
          >
            Botão Secundário
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box onClick={closeColorPickers}>
      <Typography variant="h5" gutterBottom>
        Aparência do Site
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Personalize as cores e fontes do site do seu festejo
      </Typography>

      <Grid container spacing={4}>
        {/* Configurações de Cores */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Palette />
              Cores do Tema
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cor Primária
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleColorPicker('primary');
                  }}
                  sx={{
                    width: 50,
                    height: 50,
                    backgroundColor: formData.theme.primaryColor,
                    border: '2px solid #ccc',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#999'
                    }
                  }}
                />
                <TextField
                  size="small"
                  value={formData.theme.primaryColor}
                  onChange={(e) => handleThemeUpdate({ primaryColor: e.target.value })}
                  sx={{ width: 120 }}
                />
              </Box>
              
              {colorPicker.primary && (
                <Box 
                  sx={{ mt: 2 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <HexColorPicker
                    color={formData.theme.primaryColor}
                    onChange={(color) => handleThemeUpdate({ primaryColor: color })}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cor Secundária
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleColorPicker('secondary');
                  }}
                  sx={{
                    width: 50,
                    height: 50,
                    backgroundColor: formData.theme.secondaryColor,
                    border: '2px solid #ccc',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#999'
                    }
                  }}
                />
                <TextField
                  size="small"
                  value={formData.theme.secondaryColor}
                  onChange={(e) => handleThemeUpdate({ secondaryColor: e.target.value })}
                  sx={{ width: 120 }}
                />
              </Box>
              
              {colorPicker.secondary && (
                <Box 
                  sx={{ mt: 2 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <HexColorPicker
                    color={formData.theme.secondaryColor}
                    onChange={(color) => handleThemeUpdate({ secondaryColor: color })}
                  />
                </Box>
              )}
            </Box>

            <FormControl fullWidth>
              <InputLabel>Família de Fontes</InputLabel>
              <Select
                value={formData.theme.fontFamily}
                onChange={(e) => handleThemeUpdate({ fontFamily: e.target.value })}
                label="Família de Fontes"
              >
                {fontOptions.map((font) => (
                  <MenuItem key={font.value} value={font.value}>
                    <span style={{ fontFamily: font.value }}>
                      {font.label}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* Temas Predefinidos */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Temas Predefinidos
            </Typography>
            
            <Grid container spacing={2}>
              {presetThemes.map((theme, index) => (
                <Grid item xs={6} key={index}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      '&:hover': {
                        borderColor: theme.primaryColor
                      }
                    }}
                    onClick={() => handleApplyPresetTheme(theme)}
                  >
                    <Box
                      sx={{
                        height: 60,
                        background: `linear-gradient(45deg, ${theme.primaryColor} 50%, ${theme.secondaryColor} 50%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'white',
                          fontFamily: theme.fontFamily,
                          fontWeight: 600,
                          textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        {theme.fontFamily}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Preview />
              Preview do Site
            </Typography>
            
            <PreviewCard />

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Dica:</strong> As cores escolhidas serão aplicadas em todo o site do festejo, 
                incluindo botões, títulos, destaques e elementos decorativos.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Botões de navegação */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onPrevious}
          startIcon={<NavigateBefore />}
          size="large"
        >
          Anterior
        </Button>

        <Button
          variant="contained"
          onClick={onNext}
          endIcon={<NavigateNext />}
          size="large"
        >
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

export default AppearanceForm;

